import { useCallback, useEffect, useState } from 'react';
import { missingBillService } from '../services/missingBillService';
import { billEmailService } from '../services/billEmailService';
import type { ExpectedSite, MissingTagRow } from '../lib/missingBills';

export type MissingBillsData = {
  sites: ExpectedSite[];
  tags: MissingTagRow[];
  /** The global Inbox count; null while unknown or when it could not be read. */
  unmatched: number | null;
  loading: boolean;
  /** Sites failed to load: the grid cannot be drawn. */
  error: string | null;
  /** Tags failed to load: the grid still draws, without tags. */
  tagsError: string | null;
  reloadTags: () => void;
};

/**
 * Sites and the unmatched count load on mount and when `reloadKey` changes; tags
 * also reload for the month range and on `reloadTags`. An unmatched-count failure
 * is not an error: the banner just stays out.
 */
export function useMissingBillsData(from: string, to: string, reloadKey: number): MissingBillsData {
  const [sites, setSites] = useState<ExpectedSite[]>([]);
  const [tags, setTags] = useState<MissingTagRow[]>([]);
  const [unmatched, setUnmatched] = useState<number | null>(null);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [sitesError, setSitesError] = useState<string | null>(null);
  const [tagsError, setTagsError] = useState<string | null>(null);
  const [tagsKey, setTagsKey] = useState(0);
  const reloadTags = useCallback(() => setTagsKey((k) => k + 1), []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setSitesLoading(true);
      setSitesError(null);
      try {
        const res = await missingBillService.loadExpectedSites();
        if (!mounted) return;
        if (res.success && res.data) setSites(res.data);
        else setSitesError(res.error ?? 'Could not load sites');
      } catch (err) {
        if (mounted) setSitesError(err instanceof Error ? err.message : 'Could not load sites');
      } finally {
        if (mounted) setSitesLoading(false);
      }
    };
    const loadUnmatched = async () => {
      try {
        const res = await billEmailService.getInboxCounts();
        if (mounted) setUnmatched(res.success && res.data ? res.data.unmatched : null);
      } catch {
        if (mounted) setUnmatched(null);
      }
    };
    void load();
    void loadUnmatched();
    return () => { mounted = false; };
  }, [reloadKey]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setTagsLoading(true);
      setTagsError(null);
      try {
        const res = await missingBillService.loadActiveTags(from, to);
        if (!mounted) return;
        if (res.success && res.data) setTags(res.data);
        else setTagsError(res.error ?? 'Could not load tags');
      } catch (err) {
        if (mounted) setTagsError(err instanceof Error ? err.message : 'Could not load tags');
      } finally {
        if (mounted) setTagsLoading(false);
      }
    };
    void load();
    return () => { mounted = false; };
  }, [from, to, reloadKey, tagsKey]);

  return {
    sites,
    tags,
    unmatched,
    loading: sitesLoading || tagsLoading,
    error: sitesError,
    tagsError,
    reloadTags,
  };
}
